import { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Navigate } from "react-router-dom";
import { FiRadio } from "react-icons/fi";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import {
  clearEnrollmentMap,
  fetchEnrollmentMaps,
  fetchHrDirectory,
  fetchPunchIntegration,
  saveEnrollmentMap,
  syncPunchIntegration,
  updatePunchIntegration,
} from "../../store/hr/hrSlice";
import { useHrAccess } from "../../hooks/useHrAccess";
import {
  AdminPageHeader,
  AdminPageShell,
  AdminPrimaryButton,
  AdminSoftInput,
  AdminSoftSelect,
  AdminTableCard,
  AdminTableState,
  adminTableCell,
  adminTableHead,
} from "../../components/admin/AdminPageShell";

function todayIso() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

const PunchIntegration = ({ embedded = false }: { embedded?: boolean }) => {
  const dispatch = useAppDispatch();
  const { isHrAdmin, canEditNotebook } = useHrAccess();
  const { punchIntegration, enrollmentMaps, directory, error } = useAppSelector(
    (state) => state.hr,
  );
  const verifiedDirectory = directory.filter((e) => e.verified);
  const [provider, setProvider] = useState("attendance_notebook");
  const [enabled, setEnabled] = useState(false);
  const [endpoint, setEndpoint] = useState("");
  const [tokenId, setTokenId] = useState("");
  const [clientId, setClientId] = useState("");
  const [secret, setSecret] = useState("");
  const [notes, setNotes] = useState("");
  const [message, setMessage] = useState("");
  const [newEnrollment, setNewEnrollment] = useState("");
  const [newEmp, setNewEmp] = useState("");
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [polling, setPolling] = useState(false);

  const notebook = provider === "attendance_notebook";

  useEffect(() => {
    if (!isHrAdmin && !canEditNotebook) return;
    dispatch(fetchPunchIntegration());
    dispatch(fetchEnrollmentMaps());
    dispatch(fetchHrDirectory());
  }, [dispatch, isHrAdmin, canEditNotebook]);

  useEffect(() => {
    if (!punchIntegration) return;
    setProvider(punchIntegration.provider || "none");
    setEnabled(punchIntegration.is_enabled);
    setEndpoint(punchIntegration.endpoint || "");
    setClientId(punchIntegration.client_id || "");
    setNotes(punchIntegration.notes || "");
  }, [punchIntegration]);

  const pollNow = async () => {
    setPolling(true);
    const result = await dispatch(syncPunchIntegration({ from: todayIso(), to: todayIso() }));
    setPolling(false);
    if (syncPunchIntegration.fulfilled.match(result)) {
      setMessage(result.payload);
      dispatch(fetchEnrollmentMaps());
      dispatch(fetchPunchIntegration());
    }
  };

  const save = async () => {
    const result = await dispatch(
      updatePunchIntegration({
        provider,
        is_enabled: enabled,
        endpoint,
        api_key: tokenId,
        client_id: clientId,
        webhook_secret: secret,
        notes,
      }),
    );
    if (updatePunchIntegration.rejected.match(result)) return;
    dispatch(fetchPunchIntegration());
    setTokenId("");
    setSecret("");
    setMessage("Saved. Enable the connection, map machine IDs, then sync a date.");
  };

  const assign = async (enrollmentId: string, empId: string) => {
    if (!enrollmentId.trim() || !empId) return;
    const result = await dispatch(
      saveEnrollmentMap({ enrollment_id: enrollmentId.trim(), emp_id: Number(empId) }),
    );
    if (saveEnrollmentMap.fulfilled.match(result)) {
      setNewEnrollment("");
      setNewEmp("");
      setMessage(
        `Mapped machine ID ${enrollmentId.trim()}. Punches for that employee start counting now (first punch = in, last = out), and every tap is kept in the punch log.`,
      );
      dispatch(fetchEnrollmentMaps());
    }
  };

  if (!isHrAdmin && !canEditNotebook) {
    return embedded ? null : <Navigate to="/dashboard/employee/my-attendance" replace />;
  }

  const body = (
    <>
      <section className="grid gap-4 lg:grid-cols-[1.2fr_0.8fr]">
          <div className="rounded-2xl border border-[#E8E8E4] bg-white p-5 shadow-sm">
            <div className="mb-4 flex items-center gap-3">
              <div className="rounded-xl bg-[#E8F0EE] p-3 text-[#345952]">
                <FiRadio size={22} />
              </div>
              <div>
                <p className="font-semibold text-[#4A4A48]">Notebook connection</p>
                <p className="text-sm text-[#8A8A85]">
                  POST TokenID, ClientID, StartDateTime, EndDateTime
                </p>
              </div>
            </div>

            <div className="grid gap-3">
              <label className="text-xs text-[#8A8A85]">
                Provider
                <AdminSoftSelect
                  className="mt-1 block w-full"
                  value={provider}
                  disabled={!canEditNotebook}
                  onChange={(e) => setProvider(e.target.value)}
                >
                  <option value="attendance_notebook">Attendance Notebook</option>
                  <option value="generic_webhook">Generic webhook</option>
                  <option value="none">Not connected</option>
                </AdminSoftSelect>
              </label>
              <label className="flex items-center gap-2 text-sm text-[#4A4A48]">
                <input
                  type="checkbox"
                  checked={enabled}
                  disabled={!canEditNotebook}
                  onChange={(e) => setEnabled(e.target.checked)}
                />
                Enable the 5-minute server poll
              </label>
              <AdminSoftInput
                placeholder="Punch API URL"
                value={endpoint}
                disabled={!canEditNotebook}
                onChange={(e) => setEndpoint(e.target.value)}
              />
              {notebook ? (
                <>
                  <AdminSoftInput
                    placeholder={
                      punchIntegration?.api_key
                        ? "Token ID (leave blank to keep current)"
                        : "Token ID"
                    }
                    value={tokenId}
                    disabled={!canEditNotebook}
                    onChange={(e) => setTokenId(e.target.value)}
                  />
                  <AdminSoftInput
                    placeholder="Client ID"
                    value={clientId}
                    disabled={!canEditNotebook}
                    onChange={(e) => setClientId(e.target.value)}
                  />
                </>
              ) : (
                <AdminSoftInput
                  placeholder="Webhook secret (x-punch-secret header)"
                  value={secret}
                  onChange={(e) => setSecret(e.target.value)}
                />
              )}
              <textarea
                className="min-h-[70px] rounded-xl border border-[#DDDDD8] bg-[#F8F8F6] px-3 py-2.5 text-sm outline-none focus:border-[#345952]"
                placeholder="Notes"
                value={notes}
                disabled={!canEditNotebook}
                onChange={(e) => setNotes(e.target.value)}
              />
              {canEditNotebook ? (
                <AdminPrimaryButton onClick={save}>Save connection</AdminPrimaryButton>
              ) : (
                <p className="text-sm text-[#8A8A85]">
                  Only Super Admin and Developer can edit this connection.
                </p>
              )}
            </div>
          </div>

          <div className="rounded-2xl border border-[#E8E8E4] bg-white p-5 text-sm text-[#4A4A48] shadow-sm">
            <h2 className="mb-2 font-semibold">Automatic poll</h2>
            <p className="mb-3 text-[#8A8A85]">
              The server asks Attendance Notebook for today’s punches every 5 minutes.
              Use Poll now if you want those punches immediately.
            </p>
            <AdminPrimaryButton onClick={pollNow} disabled={polling || !isHrAdmin}>
              {polling ? "Polling…" : "Poll now"}
            </AdminPrimaryButton>
            {message ? <p className="mt-3 text-sm text-emerald-700">{message}</p> : null}
            {error ? <p className="mt-3 text-sm text-red-600">{error}</p> : null}
            {punchIntegration?.last_synced_at ? (
              <p className="mt-3 text-xs text-[#8A8A85]">
                Last sync: {new Date(punchIntegration.last_synced_at).toLocaleString()}
              </p>
            ) : null}
          </div>
        </section>

        {isHrAdmin ? (
        <section className="rounded-2xl border border-[#E8E8E4] bg-white p-5 shadow-sm">
          <h2 className="font-semibold text-[#4A4A48]">Machine ID to employee</h2>
          <p className="mb-4 mt-1 text-sm text-[#8A8A85]">
            Enrollment IDs from the device are not portal employee IDs. Map each one once.
            Unmapped IDs show up here after the server poll. Their punches are skipped until you map them.
          </p>

          <div className="mb-4 flex flex-wrap items-end gap-2">
            <label className="text-xs text-[#8A8A85]">
              Machine enrollment ID
              <AdminSoftInput
                className="mt-1 block"
                placeholder="00000022"
                value={newEnrollment}
                onChange={(e) => setNewEnrollment(e.target.value)}
              />
            </label>
            <label className="text-xs text-[#8A8A85]">
              Employee
              <AdminSoftSelect
                className="mt-1 block"
                value={newEmp}
                onChange={(e) => setNewEmp(e.target.value)}
              >
                <option value="">Select employee</option>
                {verifiedDirectory.map((e) => (
                  <option key={e.emp_id} value={e.emp_id}>
                    {e.emp_name} (emp {e.emp_id})
                  </option>
                ))}
              </AdminSoftSelect>
            </label>
            <AdminPrimaryButton
              disabled={!newEnrollment.trim() || !newEmp}
              onClick={() => assign(newEnrollment, newEmp)}
            >
              Map ID
            </AdminPrimaryButton>
          </div>

          <AdminTableCard>
            <table className="w-full min-w-[720px]">
              <thead>
                <tr className="border-b border-[#E8E8E4] bg-[#FDFBF2]">
                  <th className={adminTableHead}>Machine ID</th>
                  <th className={adminTableHead}>Employee</th>
                  <th className={adminTableHead}>Last punch seen</th>
                  <th className={adminTableHead}>Device</th>
                  <th className={adminTableHead}></th>
                </tr>
              </thead>
              <tbody>
                {enrollmentMaps.length === 0 ? (
                  <AdminTableState
                    colSpan={5}
                    variant="empty"
                    message="No machine IDs yet. Sync a day or add a mapping above."
                  />
                ) : (
                  enrollmentMaps.map((row) => (
                    <tr key={row.map_id} className="border-b border-[#F0F0EC]">
                      <td className={adminTableCell}>{row.enrollment_id}</td>
                      <td className={adminTableCell}>
                        <AdminSoftSelect
                          value={drafts[row.enrollment_id] ?? (row.emp_id ? String(row.emp_id) : "")}
                          onChange={(e) =>
                            setDrafts((prev) => ({
                              ...prev,
                              [row.enrollment_id]: e.target.value,
                            }))
                          }
                        >
                          <option value="">Unmapped</option>
                          {verifiedDirectory.map((e) => (
                            <option key={e.emp_id} value={e.emp_id}>
                              {e.emp_name}
                            </option>
                          ))}
                        </AdminSoftSelect>
                      </td>
                      <td className={adminTableCell}>
                        {row.last_punch_at ? new Date(row.last_punch_at).toLocaleString() : "—"}
                      </td>
                      <td className={adminTableCell}>{row.device_id || "—"}</td>
                      <td className={adminTableCell}>
                        <button
                          type="button"
                          className="mr-2 text-xs font-medium text-[#345952]"
                          onClick={() =>
                            assign(
                              row.enrollment_id,
                              drafts[row.enrollment_id] ?? (row.emp_id ? String(row.emp_id) : ""),
                            )
                          }
                        >
                          Save
                        </button>
                        {row.emp_id ? (
                          <button
                            type="button"
                            className="text-xs text-[#8A8A85]"
                            onClick={async () => {
                              await dispatch(clearEnrollmentMap(row.enrollment_id));
                              dispatch(fetchEnrollmentMaps());
                            }}
                          >
                            Clear
                          </button>
                        ) : null}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </AdminTableCard>
        </section>
        ) : null}
    </>
  );

  if (embedded) return body;

  return (
    <>
      <Helmet>
        <title>Punch API</title>
      </Helmet>
      <AdminPageShell accent="#345952">
        <AdminPageHeader
          title="Attendance Notebook"
          subtitle="Machine connection and enrollment mapping. Punch times are on the attendance log above."
        />
        {body}
      </AdminPageShell>
    </>
  );
};

export default PunchIntegration;
